import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { GlobalModalComponent, GlobalModalConfig } from 'src/app/components/global-modal/global-modal.component';
import { FinancialsService } from 'src/app/services/modules/financials/financials.service';
import { formatMoney } from '../financial/financial-grid';

export interface BatchTransaction {
  reference: string;
  state: number | string;
  amount: string;
  currency: string;
}

export interface ItemDetail {
  status: boolean;
  item_id: string;
  booking_id: number;
  name: string;
  description: string | null;
  /** Zai's raw state, e.g. "payment_deposited" or "completed". */
  payment_status: string | null;
  amount_released: string | null;
  batch_transactions: BatchTransaction[];
}

/** "payment_deposited" -> "Payment deposited". */
export function humanizeStatus(value: string | null | undefined): string {
  const text = (value ?? '').replace(/[_-]+/g, ' ').trim().toLowerCase();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
}

@Component({
  selector: 'app-disbursement-report-modal',
  standalone: true,
  imports: [
    CommonModule,
    GlobalModalComponent,
  ],
  templateUrl: './disbursement-report-modal.component.html',
  styleUrls: [
    './disbursement-report-modal.component.scss',
  ],
})
export class DisbursementReportModalComponent implements OnInit, OnDestroy {
  /** The row's Zai escrow item. */
  @Input({ required: true }) itemId!: number;
  /** The row's booking, for the title while the report loads. */
  @Input() bookingId?: number;

  detail: ItemDetail | null = null;
  loading = true;
  errorMessage = '';
  /** Only a failure that can clear up (Zai down, no connection) is worth retrying. */
  canRetry = false;

  private request?: Subscription;

  constructor(private financialsService: FinancialsService) {}

  get config(): GlobalModalConfig {
    const booking = this.detail?.booking_id ?? this.bookingId;
    return {
      title: 'Disbursement Report',
      subtitle: booking ? `Booking #${booking}` : undefined,
      align: 'left',
      hideCancel: true,
      confirmText: 'Close',
      confirmBtnClass: 'btn-outline-secondary',
    };
  }

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.request?.unsubscribe();
  }

  load(): void {
    this.request?.unsubscribe();
    this.loading = true;
    this.errorMessage = '';
    this.request = this.financialsService.getProviderItemDetail(this.itemId).subscribe({
      next: (res: ItemDetail) => {
        this.detail = { ...res, batch_transactions: res?.batch_transactions ?? [] };
        this.loading = false;
      },
      error: (err) => {
        const status = err?.status;
        this.errorMessage = err?.error?.message || (status === 404 ? 'Item not found.' : 'Payment details are unavailable right now.');
        this.canRetry = status === 0 || status === 502 || status === 503 || status === 504;
        this.loading = false;
      },
    });
  }

  money(value: unknown): string {
    return formatMoney(value) || '—';
  }

  status(value: string | null | undefined): string {
    return humanizeStatus(value) || '—';
  }

  trackPayout(index: number, payout: BatchTransaction): string {
    return `${payout.reference}-${index}`;
  }
}
